import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  CallIntelConstant,
  CallIntelligenceV2,
  CallIntelQueries,
  CallIntelTypes,
  CallIntelUtils,
} from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import { useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { Chart, XAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { formatters } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { callIntelligenceUtils } from '../../../utils/call-intelligence';
import { OverviewSummaryOpportunitiesStackV2 } from '../charts/overview-summary-opportunities-stack';
import { SentimentsChart } from '../charts/sentiments-chart';
import { CustomTooltipTitle } from '../custom-tooltip-title';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore } from '../hooks/use-call-intel-store';
import { SubViewOpportunitiesInlineStatsV2 } from './sub-view-opportunities-inline-stats';

type Props = {
  metricsFor?: string;
  onChangeLoadingState?: (isLoading: boolean) => void;
};

export const OpportunitiesByTypeSubView = ({ metricsFor, onChangeLoadingState }: Props) => {
  const { t } = useTranslation('analytics');
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { chartLabelColors, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
    'chartLabelColors',
    'dataColors',
    'dataLabels'
  );
  const { subView } = useCallIntelShallowStore('subView');
  const { filterHintText, filters } = useActiveFilters();

  const drillDownFilters = useMemo(() => callIntelligenceUtils.getDrillDownFilterKeys(subView), [subView]);

  const overviewDetailsRequestPayload = useMemo(
    () => ({
      drillDownOptions: drillDownFilters,
      filters,
      includesAnalysis: [
        subView.type === 'appointment-type'
          ? CallIntelTypes.IncludesAnalysis.CALL_CATEGORY
          : CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENT_TYPE,
        CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT,
      ],
      includesHydration: [],
    }),
    [drillDownFilters, filters, subView.type]
  );

  const { data: overviewDetails, isFetching: isLoadingOverviewDetails } = CallIntelQueries.useGetOverviewDetails({
    payload: overviewDetailsRequestPayload,
    realQueryOptions: {
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        sentry.warn({
          team: 'analytics',
          topic: 'call-intelligence',
          error: `Error fetching overview details: ${error}`,
          addContext: {
            name: 'filters',
            context: { payload: overviewDetailsRequestPayload },
          },
        });
      },
    },
  });

  const schedulingAggregateStatsRequestPayload = useMemo(
    () => CallIntelUtils.generateChartsPayload({ filters, drillDownOptions: drillDownFilters }),
    [drillDownFilters, filters]
  );

  const { data: schedulingStats, isLoading: isLoadingSchedulingStats } =
    CallIntelligenceV2.Queries.useGetSchedulingAggregateStatsQuery({
      request: schedulingAggregateStatsRequestPayload,
      options: {
        enabled: !!filters.locations?.length,
        retry: false,
        cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
        staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
        onError: (error) => {
          sentry.warn({
            team: 'analytics',
            topic: 'call-intelligence',
            error: `Error fetching scheduling aggregate stats: ${error}`,
            addContext: {
              name: 'filters',
              context: { payload: schedulingAggregateStatsRequestPayload },
            },
          });
        },
      },
    });

  const targetedChartData = useMemo(() => {
    const isAppointmentType = subView.type === 'appointment-type';
    const subViewLabel = isAppointmentType
      ? dataLabels.appointmentTypes?.[subView.id || '']
      : dataLabels.categories?.[subView.id || ''];

    const titleKey = isAppointmentType ? '{{name}} Calls - Call Categories' : '{{name}} Calls - Appointment Types';

    return {
      colors: isAppointmentType ? dataColors.categories : dataColors.appointmentTypes,
      data: isAppointmentType ? overviewDetails?.categories : overviewDetails?.appointmentTypes,
      labelColors: isAppointmentType ? chartLabelColors.categories : chartLabelColors.appointmentTypes,
      labels: isAppointmentType ? dataLabels.categories : dataLabels.appointmentTypes,
      title: t(titleKey, { name: subViewLabel }),
    };
  }, [
    chartLabelColors.appointmentTypes,
    chartLabelColors.categories,
    dataColors.appointmentTypes,
    dataColors.categories,
    dataLabels.appointmentTypes,
    dataLabels.categories,
    overviewDetails?.appointmentTypes,
    overviewDetails?.categories,
    subView.id,
    subView.type,
    t,
  ]);

  const barChartGroups = useMemo(() => {
    return {
      groups: Object.entries(targetedChartData.data || {}).map(([name, value]) => ({
        name,
        values: {
          [name]: value,
        },
      })),
    };
  }, [targetedChartData]);

  const renderCustomTooltipTitle = useCallback(
    ({ groupName }: { groupName: string }) => (
      <CustomTooltipTitle addPrefix label={targetedChartData.labels?.[groupName]} />
    ),
    [targetedChartData.labels]
  );

  const renderCustomXAxisTick = useCallback(
    ({ labels, groupName, ...rest }: any) => (
      <XAxisLabelValueTick
        {...rest}
        color={targetedChartData.labelColors?.[groupName]}
        label={labels?.[groupName] || groupName}
        value={formatters.value(targetedChartData.data?.[groupName as keyof typeof targetedChartData.data])}
      />
    ),
    [targetedChartData]
  );

  const getCustomTooltipData = useCallback(() => {
    if (!targetedChartData?.data) return [];
    return Object.entries(targetedChartData.data).map(([key, value]) => ({
      id: key,
      label: targetedChartData.labels?.[key] || key,
      color: targetedChartData.labelColors?.[key] || theme.colors.neutral50,
      formattedValue: formatters.value(value),
    }));
  }, [targetedChartData]);

  useEffect(() => {
    onChangeLoadingState?.(isLoadingOverviewDetails || isLoadingSchedulingStats);
  }, [isLoadingSchedulingStats, onChangeLoadingState, isLoadingOverviewDetails]);

  return (
    <>
      <SubViewOpportunitiesInlineStatsV2
        data={schedulingStats?.current}
        isLoading={isLoadingSchedulingStats}
        metricsFor={metricsFor}
      />

      <OverviewSummaryOpportunitiesStackV2 data={schedulingStats?.current} isLoading={isLoadingSchedulingStats} />

      <Chart.HorizontalContainer marginBottom={0} wrapperStyles={styles.container}>
        <Chart
          colors={targetedChartData.colors}
          commonTooltipLabel={t('Calls')}
          isLoading={isLoadingOverviewDetails}
          labels={targetedChartData.labels}
          css={styles.chartStyles}
        >
          <Chart.Header
            leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
            subtitle={filterHintText}
            title={targetedChartData.title}
            removeHeaderBottomMargin
          />
          <Chart.BarChart
            appearance={{
              customTooltipTitle: renderCustomTooltipTitle,
              customXAxisTick: renderCustomXAxisTick,
              customTooltipData: getCustomTooltipData,
              margin: { bottom: 20 },
              showXAxis: true,
            }}
            data={barChartGroups}
            formatValue={formatters.value}
          />
        </Chart>
        <SentimentsChart internalPage isLoading={isLoadingOverviewDetails} sentiments={overviewDetails?.sentiments} />
      </Chart.HorizontalContainer>
    </>
  );
};

const styles = {
  container: css`
    gap: ${theme.spacing(3)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      gap: ${theme.spacing(2)};
    }
  `,
  chartStyles: css`
    .chart-content {
      padding: ${theme.spacing(3)};
    }
  `,
};
